// Page furniture: the header row and the empty state.
//
// Not from Zanetto Tools — it repeats this markup on every page. Having it once
// is what stops the eleventh page inventing its own spacing, which is the drift
// this whole pass exists to undo.
import type { ReactNode } from 'react';

/**
 * Title on the left, actions on the right.
 *
 * At most ONE `btn-primary` belongs in `actions` — that is the design system's
 * rule, not a suggestion. Everything else is `btn-secondary` or goes in an
 * ActionMenu.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="!mb-0 text-xl font-semibold text-gray-900">{title}</h1>
        {subtitle && <p className="!mb-0 mt-0.5 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * What a list shows when it has nothing in it.
 *
 * A new tenant sees a lot of these — the trial starts genuinely empty — so it
 * gets a real treatment and, where there is one, the action that fills it. An
 * empty state that only says "None" teaches nobody what to do next.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="card p-10 text-center">
      <p className="!mb-0 text-sm font-medium text-gray-900">{title}</p>
      {body && <p className="!mb-0 mx-auto mt-1 max-w-sm text-sm text-gray-500">{body}</p>}
      {action && <div className="mt-5 flex justify-center gap-2">{action}</div>}
    </div>
  );
}
